import { FormikErrors, useFormik } from 'formik';
import { TFunction } from 'i18next';
import React, { FC, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import * as Yup from 'yup';

import { CREATE_ORDER_SIDE, DEPTH, ORDER_TYPE, PRICE_TYPE, MAX_INPUT } from 'src/common/constants';
import {
    changeScrollBehavior,
    digits,
    isEmptyObj,
    roundNumber,
    setCurrentUrl,
} from 'src/common/helpers';
import { Input, Button, Tabs, Tab, Range } from 'src/components/core';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { ConfirmationModal } from 'src/components/features/spot/modals/confirmationModal';
import { useAuth } from 'src/context/AuthContext';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { useKycAndFeaturesConfig } from 'src/hooks/useKycAndFeaturesConfig';
import { APP_URLS } from 'src/setup/client/app-urls';
import { createOrder } from 'src/setup/redux/slices/spotSlice';
import {
    TradeSocketResponseData,
    SpotFormElements,
    Symbol,
    SpotAsset,
    SpotOrderSetting,
} from 'src/types';

const StyledForm = styled.form`
    ${({ theme: { typography, colors, isMobile } }) => `
       .tabs {
            display: flex;
            padding: 0;
            & > .tab {
                flex: 1;
                justify-content: center;
                font-size: ${typography.size.desktop.paragraph.p3};
                margin: 0px;
            }
        }
        & > .order-side {
            padding: 0px 30px;
        }
        & > .form-elements {
            padding: 18px 13px 16px 13px;
            & > * {
                margin-bottom: 18px;
            }
            & > .info-container {
                justify-content: space-between;
                & > .order-value {
                    display: ${isMobile ? 'flex' : 'block'}
                }
            }
            .cost-container {
                justify-content: space-between;
                .separator {
                    color: ${colors.text.tertiary}
                }
                .value-container {
                    word-break: break-word;
                    margin-left: 20px;
                    text-align: right;
                    font-size: ${typography.size.desktop.paragraph.p3};
                }
            }
            .price-type {
                display: inline-flex;
                & > .tab {
                    padding: 6px;
                    margin-right: 4px;
                }
            }
        }
        .buttons-container {
            .button{
                color: ${colors.brand.main};
                padding: 10px 0px;
            }
            .left-button {
                margin-right: 7.5px;
            }
            .right-button {
                margin-left: 7.5px;
            }
        }
    `}
`;

const LabelText = styled.div`
    ${({ theme: { typography, colors, isMobile } }) => `
        font-size: ${typography.size.desktop.paragraph.p3};
        color:${colors.text.tertiary};
        margin-right: ${isMobile ? '8px' : '0px'};
         white-space: nowrap;
    `}
`;

const ValueText = styled.span`
    ${({ theme: { typography } }) => `
        font-size: ${typography.size.desktop.paragraph.p3};
        word-break: break-word;
         white-space: nowrap;
    `}
`;

const InputRightLabel = styled.span`
    ${({ theme: { typography, colors } }) => `
        font-size: ${typography.size.desktop.paragraph.p3};
        color: ${colors.text.secondary};
        margin-right: 10px;
         white-space: nowrap;
    `}
`;

export const initialValues = {
    orderSide: CREATE_ORDER_SIDE.BUY,
    orderType: ORDER_TYPE.MARKET,
    priceType: PRICE_TYPE.MARKET,
    price: '',
    triggerPrice: '',
    amount: '',
    quantity: '',
    percentage: 0,
};

const validationSchema = (
    maxCloseAble: number,
    minTradeQuantity: number,
    minTradeAmount: number,
    t: TFunction<'translation', undefined>,
) => {
    return Yup.object().shape({
        orderSide: Yup.string().required(),
        orderType: Yup.string().required(),
        priceType: Yup.string().required(),
        price: Yup.mixed()
            .when('priceType', {
                is: PRICE_TYPE.MARKET,
                then: Yup.string(),
                otherwise: Yup.number().moreThan(
                    0,
                    t('form_validation.greater', { field: t('order_price'), length: '0' }),
                ),
            })
            .required(t('form_validation.field_required', { field: t('order_price') })),
        triggerPrice: Yup.number().when('orderType', {
            is: ORDER_TYPE.CONDITIONAL,
            then: Yup.number()
                .moreThan(
                    0,
                    t('form_validation.greater', { field: t('trigger_price'), length: '0' }),
                )
                .required(t('form_validation.field_required', { field: t('trigger_price') })),
            otherwise: Yup.number(),
        }),
        amount: Yup.number().when(['orderType', 'orderSide'], {
            is: (orderType: string, orderSide: string) =>
                orderType === ORDER_TYPE.MARKET && orderSide === CREATE_ORDER_SIDE.SELL,
            then: Yup.number().nullable(),
            otherwise: Yup.number()
                .min(
                    minTradeAmount,
                    t('form_validation.cannot_less', {
                        field: t('amount'),
                        length: `${minTradeAmount}`,
                    }),
                )
                .required(t('form_validation.field_required', { field: t('amount') })),
        }),
        quantity: Yup.number()
            .min(
                minTradeQuantity,
                t('form_validation.cannot_less', {
                    field: t('quantity'),
                    length: `${minTradeQuantity}`,
                }),
            )
            .when(['orderSide'], {
                is: (orderSide: string) => orderSide === CREATE_ORDER_SIDE.SELL,
                then: Yup.number().max(
                    maxCloseAble,
                    t('form_validation.cannot_greater', {
                        field: t('quantity'),
                        length: `${maxCloseAble}`,
                    }),
                ),
                otherwise: Yup.number(),
            })
            .required(t('form_validation.field_required', { field: t('quantity') })),
        percentage: Yup.number().required(),
    });
};

const Form: FC = () => {
    const { colors, isMobile } = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { allowTrading } = useKycAndFeaturesConfig();

    const [isOrderConfirmationOpen, setIsOrderConfirmationOpen] = useState(false);
    const [orderConfirmationCheckLower, setOrderConfirmationCheckLower] = useState(false);
    const [orderConfirmationCheckHigher, setOrderConfirmationCheckHigher] = useState(false);
    const [orderConfirmationCheckTrigger, setOrderConfirmationCheckTrigger] = useState(false);
    const [tokenInfo, setTokenInfo] = useState({} as TradeSocketResponseData);
    const [shouldSetPrice, setShouldSetPrice] = useState(true);
    const [minTradeQuantity, setMinTradeQuantity] = useState('');
    const [totalFee, setTotalFee] = useState(0);

    const { isAuthenticated } = useAuth();

    const tokenInfoData = useAppSelector((state) => state.socket.slowBroker);
    const { userAccountLevelInfo } = useAppSelector((state) => state.user);
    const { currentSymbol, createOrderPrice } = useAppSelector((state) => state.spot);
    const createOrderLoading = useAppSelector((state) => state.spot.loading.createOrder);
    const {
        quoteTokenName,
        quoteTokenId,
        symbolId,
        baseTokenName,
        baseTokenId,
        minTradeQuantity: tokenMinTradeQuanitty,
        minTradeAmount,
        minPricePrecision,
        basePrecision,
        quotePrecision,
    }: Symbol = useAppSelector((state) => state.spot.currentSymbol);

    //order setting
    const { isConfirm }: SpotOrderSetting = useAppSelector((state) => state.spot.orderSetting);

    //assets
    const currentAsset: SpotAsset[] = useAppSelector((state) => state.spot.assets);
    const quoteAssets = currentAsset.find((item) => item.tokenId === quoteTokenId);
    const baseAssets = currentAsset.find((item) => item.tokenId === baseTokenId);

    const maxTradeQuantityDecimals = DEPTH[basePrecision];
    const maxPriceDecimals = DEPTH[minPricePrecision];
    const maxMarginDecimals = DEPTH[quotePrecision];

    const orderButtonRef = useRef('');

    const {
        values,
        setFieldValue,
        setValues,
        errors,
        handleBlur,
        setErrors,
        validateForm,
        handleSubmit,
    } = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema(
            Number(baseAssets !== undefined ? baseAssets?.free : 0.0),
            Number(minTradeQuantity),
            Number(minTradeAmount),
            t,
        ),
        validateOnMount: false,
        validateOnBlur: false,
        validateOnChange: false,
        onSubmit: (values) => {
            const order = {
                side: values.orderSide,
                type:
                    values.priceType === PRICE_TYPE.LIMIT
                        ? ORDER_TYPE.LIMIT.toLowerCase()
                        : ORDER_TYPE.MARKET.toLowerCase(),
                ...(values.priceType === PRICE_TYPE.LIMIT && { price: values.price }),
                ...(values.orderType === ORDER_TYPE.CONDITIONAL && {
                    trigger_price: values.triggerPrice,
                }),
                ...(values.orderSide === CREATE_ORDER_SIDE.BUY
                    ? {
                          quantity:
                              values.priceType === PRICE_TYPE.LIMIT
                                  ? values.quantity
                                  : values.amount,
                      }
                    : { quantity: values.quantity }),
                symbol_id: symbolId,
                client_order_id: new Date().getTime(),
            };
            dispatch(createOrder(order));
        },
    });

    let updatedValues: SpotFormElements = values;
    let updatedErrors: FormikErrors<SpotFormElements> = errors;

    // regular expression to validate quantity
    const QuantityRegEx = new RegExp(
        `^[0-9]*${
            maxTradeQuantityDecimals > 0
                ? // eslint-disable-next-line prefer-template
                  '(\\.[0-9]{0,' + maxTradeQuantityDecimals + '})?$'
                : '?$'
        }`,
    );

    // regular expression to validate price
    const PriceRegEx = new RegExp(
        `^[0-9]*${
            maxPriceDecimals > 0
                ? // eslint-disable-next-line prefer-template
                  '(\\.[0-9]{0,' + maxPriceDecimals + '})?$'
                : '?$'
        }`,
    );

    // regular expression to validate amount
    const AmountRegEx = new RegExp(
        `^[0-9]*${
            maxMarginDecimals > 0
                ? // eslint-disable-next-line prefer-template
                  '(\\.[0-9]{0,' + maxMarginDecimals + '})?$'
                : '?$'
        }`,
    );

    const toggleOrderConfirmation = () => {
        changeScrollBehavior(!isOrderConfirmationOpen);
        setIsOrderConfirmationOpen(!isOrderConfirmationOpen);
    };

    const getPrice = ({ price, priceType }: SpotFormElements) => {
        let p = 0;
        if (priceType === PRICE_TYPE.LIMIT) {
            p = Number(price);
        } else if (priceType === PRICE_TYPE.MARKET) {
            p = Number(tokenInfo.c);
        }
        return p;
    };

    const calculateFeeRate = () => {
        const { fees, userLevelInfo } = userAccountLevelInfo;

        if (!fees) return 0;

        const isTaker = updatedValues.orderType === ORDER_TYPE.MARKET;
        const isBuyOrder = orderButtonRef.current === CREATE_ORDER_SIDE.BUY;

        let fee, discount;

        if (isBuyOrder) {
            fee = isTaker ? fees.spotBuyTakerFee : fees.spotBuyMakerFee;
            discount = isTaker
                ? userLevelInfo.spotBuyTakerDiscount
                : userLevelInfo.spotBuyMakerDiscount;
        } else {
            fee = isTaker ? fees.spotSellTakerFee : fees.spotSellMakerFee;
            discount = isTaker
                ? userLevelInfo.spotSellTakerDiscount
                : userLevelInfo.spotSellMakerDiscount;
        }

        return Number(fee) * Number(discount);
    };

    const calculateQuantity = (formData: SpotFormElements) => {
        return roundNumber(
            Number(formData.amount) / getPrice(formData),
            maxTradeQuantityDecimals,
        ).toString();
    };

    const calculateAmount = (formData: SpotFormElements) => {
        return roundNumber(
            Number(formData.quantity) * getPrice(formData),
            maxMarginDecimals,
        ).toString();
    };

    const handleChange = (
        value: string | number | number[] | boolean,
        name: string | undefined,
    ) => {
        //don't change when change is not according to Regex or greater than max input
        if (
            (name === 'quantity' && !QuantityRegEx.test(value.toString())) ||
            ((name === 'price' || name === 'triggerPrice') && !PriceRegEx.test(value.toString())) ||
            (name === 'amount' && !AmountRegEx.test(value.toString())) ||
            Number(value) > MAX_INPUT
        ) {
            return;
        }

        // if value is . append 0 before .
        value = value === '.' ? '0.' : value;

        updatedValues = { ...values, [name!]: value };
        updatedErrors = { ...errors, [name!]: undefined };

        if (name === 'orderSide') {
            updatedValues.amount = '';
            updatedValues.quantity = '';
            updatedValues.percentage = 0;
            updatedValues.triggerPrice = '';
            updatedValues.orderType = ORDER_TYPE.MARKET;
            updatedValues.priceType = PRICE_TYPE.MARKET;
            updatedValues.price = t('market_price');
            updatedErrors = {};
        } else if (name === 'orderType') {
            if (value === ORDER_TYPE.MARKET) {
                updatedValues.price = t('market_price');
                updatedValues.priceType = PRICE_TYPE.MARKET;
            } else {
                updatedValues.price = tokenInfo.c;
                updatedValues.priceType = PRICE_TYPE.LIMIT;
            }
            updatedValues.amount = '';
            updatedValues.quantity = '';
            updatedValues.triggerPrice = '';
            updatedValues.percentage = 0;
            updatedErrors = {};
        } else if (name === 'percentage') {
            if (isAuthenticated) {
                if (
                    Number(updatedValues.price) > 0 ||
                    updatedValues.priceType === PRICE_TYPE.MARKET
                ) {
                    if (updatedValues.orderSide === CREATE_ORDER_SIDE.BUY) {
                        if (quoteAssets && Number(quoteAssets.free) > 0) {
                            updatedValues.amount = roundNumber(
                                (Number(updatedValues.percentage) * Number(quoteAssets?.free)) /
                                    100,
                                maxMarginDecimals,
                            ).toString();
                            updatedValues.quantity = calculateQuantity(updatedValues);
                        }
                    } else {
                        if (baseAssets && Number(baseAssets.free) > 0) {
                            updatedValues.quantity = roundNumber(
                                (Number(updatedValues.percentage) * Number(baseAssets?.free)) / 100,
                                maxTradeQuantityDecimals,
                            ).toString();
                            updatedValues.amount = calculateAmount(updatedValues);
                        }
                    }
                }
            }
            updatedErrors.quantity = undefined;
            updatedErrors.amount = undefined;
        } else if (name === 'amount') {
            if (Number(updatedValues.price) > 0 || updatedValues.priceType === PRICE_TYPE.MARKET) {
                updatedValues.quantity = calculateQuantity(updatedValues);
                updatedErrors.quantity = undefined;
            }
        } else if (name === 'quantity') {
            if (Number(updatedValues.price) > 0 || updatedValues.priceType === PRICE_TYPE.MARKET) {
                updatedValues.amount = calculateAmount(updatedValues);
                updatedErrors.amount = undefined;
            }
        } else if (name === 'price') {
            updatedValues.quantity = '';
            updatedValues.amount = '';
            updatedErrors.amount = undefined;
            updatedErrors.quantity = undefined;
            updatedValues.percentage = 0;
        }

        // calculate progress
        if (name === 'amount' || name === 'quantity') {
            if (updatedValues.orderSide === CREATE_ORDER_SIDE.BUY) {
                if (quoteAssets && Number(quoteAssets?.free) > 0) {
                    updatedValues.percentage = Math.min(
                        roundNumber(
                            (Number(updatedValues.amount) * 100) / Number(quoteAssets?.free),
                            0,
                        ),
                        100,
                    );
                }
            } else {
                if (baseAssets && Number(baseAssets?.free) > 0) {
                    updatedValues.percentage = Math.min(
                        roundNumber(
                            (Number(updatedValues.quantity) * 100) / Number(baseAssets?.free),
                            0,
                        ),
                        100,
                    );
                }
            }
        }
        setValues(updatedValues);
        setErrors(updatedErrors);
    };

    const handleFormSubmit = async () => {
        if (!allowTrading) return;

        setOrderConfirmationCheckHigher(false);
        setOrderConfirmationCheckLower(false);
        setOrderConfirmationCheckTrigger(false);
        const priceConfirm = (Number(updatedValues.price) * 100) / Number(tokenInfo.c);

        const result = await validateForm();
        if (isEmptyObj(result)) {
            setTotalFee(
                digits(Number(updatedValues.amount) * calculateFeeRate(), maxMarginDecimals),
            );
            if (updatedValues.priceType !== PRICE_TYPE.MARKET) {
                if (priceConfirm <= 80) {
                    setOrderConfirmationCheckLower(true);
                    toggleOrderConfirmation();
                    return;
                } else if (priceConfirm >= 120) {
                    setOrderConfirmationCheckHigher(true);
                    toggleOrderConfirmation();
                    return;
                } else if (
                    updatedValues.orderType === ORDER_TYPE.CONDITIONAL &&
                    Number(updatedValues.price) > Number(updatedValues.triggerPrice)
                ) {
                    setOrderConfirmationCheckTrigger(true);
                    toggleOrderConfirmation();
                    return;
                }
            }
            if (isConfirm) {
                toggleOrderConfirmation();
                return;
            }
            handleSubmit();
        }
    };

    // calculate the min quantity
    useEffect(() => {
        if (Object.keys(tokenInfo).length > 0) {
            if (
                updatedValues.orderSide === CREATE_ORDER_SIDE.SELL &&
                updatedValues.orderType === ORDER_TYPE.MARKET
            ) {
                setMinTradeQuantity(tokenMinTradeQuanitty);
            } else {
                setMinTradeQuantity(
                    calculateQuantity({
                        ...updatedValues,
                        amount: minTradeAmount,
                    }),
                );
            }
        }
    }, [minTradeAmount, updatedValues, tokenInfo, tokenMinTradeQuanitty]);

    // update token info
    useEffect(() => {
        let infoObj = tokenInfoData[symbolId];
        infoObj && setTokenInfo(infoObj);
    }, [tokenInfoData, symbolId]);

    // set last traded price as price value on load
    useEffect(() => {
        if (shouldSetPrice && tokenInfo.c) {
            setFieldValue(
                'price',
                values.priceType === PRICE_TYPE.MARKET ? t('market_price') : tokenInfo.c,
            );
            setShouldSetPrice(false);
        }
    }, [symbolId, tokenInfo]);

    /* reset form on pair change started */
    const resetForm = () => {
        setValues({
            ...initialValues,
            orderType:
                values.orderType === ORDER_TYPE.LIMIT
                    ? ORDER_TYPE.LIMIT
                    : values.orderType === ORDER_TYPE.CONDITIONAL
                    ? ORDER_TYPE.CONDITIONAL
                    : ORDER_TYPE.MARKET,
            priceType:
                values.priceType === PRICE_TYPE.MARKET ? PRICE_TYPE.MARKET : PRICE_TYPE.LIMIT,
            price: tokenInfo.c,
        });
        orderButtonRef.current = '';
        setErrors({});
    };

    useEffect(() => {
        resetForm();
    }, [symbolId]);
    /* reset form on pair change ended */

    useEffect(() => {
        setShouldSetPrice(true);
    }, [currentSymbol]);

    useEffect(() => {
        values.priceType !== PRICE_TYPE.MARKET && setFieldValue('price', createOrderPrice);
    }, [createOrderPrice]);

    return (
        <>
            <StyledForm>
                <Tabs
                    disabled={!allowTrading}
                    className="order-side"
                    name="orderSide"
                    styles={{ borderBottom: `1px solid ${colors.border.light}` }}
                    value={values.orderSide}
                    onChange={handleChange}
                >
                    {Object.keys(CREATE_ORDER_SIDE).map((tab, index) => {
                        return (
                            <Tab
                                value={CREATE_ORDER_SIDE[tab as keyof typeof CREATE_ORDER_SIDE]}
                                key={index}
                                color={
                                    CREATE_ORDER_SIDE[tab as keyof typeof CREATE_ORDER_SIDE] ===
                                    CREATE_ORDER_SIDE.BUY
                                        ? colors.alert.green
                                        : colors.alert.red
                                }
                                label={t(tab.toLowerCase())}
                                className={
                                    CREATE_ORDER_SIDE[tab as keyof typeof CREATE_ORDER_SIDE] ===
                                    CREATE_ORDER_SIDE.BUY
                                        ? 'OrderSide'
                                        : ''
                                }
                            />
                        );
                    })}
                </Tabs>
                <div className="form-elements">
                    <Tabs
                        disabled={!allowTrading}
                        className="order-type"
                        name="orderType"
                        value={values.orderType}
                        onChange={handleChange}
                    >
                        {Object.keys(ORDER_TYPE).map((tab, index) => {
                            return (
                                <Tab
                                    value={ORDER_TYPE[tab as keyof typeof ORDER_TYPE]}
                                    key={index}
                                    label={t(tab.toLowerCase())}
                                    isButton={true}
                                />
                            );
                        })}
                    </Tabs>
                    {values.orderType === ORDER_TYPE.CONDITIONAL && (
                        <>
                            <Input
                                disabled={!allowTrading}
                                name="triggerPrice"
                                label={t('trigger_price')}
                                containerStyles={{
                                    paddingRight: 0,
                                }}
                                value={values.triggerPrice}
                                error={errors['triggerPrice']}
                                onChange={!allowTrading ? undefined : handleChange}
                                onBlur={handleBlur}
                                rightElement={<InputRightLabel>{quoteTokenName}</InputRightLabel>}
                            />
                            <Tabs
                                disabled={!allowTrading}
                                className="price-type"
                                name="priceType"
                                value={values.priceType}
                                onChange={!allowTrading ? undefined : handleChange}
                            >
                                {Object.keys(PRICE_TYPE).map((tab, index) => {
                                    return (
                                        <Tab
                                            value={PRICE_TYPE[tab as keyof typeof PRICE_TYPE]}
                                            key={index}
                                            label={t(tab.toLowerCase())}
                                            isButton={true}
                                        />
                                    );
                                })}
                            </Tabs>
                        </>
                    )}
                    {values.priceType !== PRICE_TYPE.MARKET && (
                        <Input
                            name="price"
                            label={t('order_price')}
                            containerStyles={{
                                paddingRight: 0,
                            }}
                            value={values.price}
                            disabled={values.priceType === PRICE_TYPE.MARKET || !allowTrading}
                            error={errors['price']}
                            onChange={!allowTrading ? undefined : handleChange}
                            onBlur={handleBlur}
                            rightElement={<InputRightLabel>{quoteTokenName}</InputRightLabel>}
                        />
                    )}
                    {(values.orderSide === CREATE_ORDER_SIDE.SELL ||
                        values.priceType !== PRICE_TYPE.MARKET) && (
                        <Input
                            disabled={!allowTrading}
                            name="quantity"
                            label={t('quantity')}
                            containerStyles={{
                                paddingRight: 0,
                            }}
                            value={values.quantity}
                            error={errors['quantity']}
                            onChange={!allowTrading ? undefined : handleChange}
                            onBlur={handleBlur}
                            fullWidth
                            rightElement={<InputRightLabel>{baseTokenName}</InputRightLabel>}
                        />
                    )}
                    {values.orderSide === CREATE_ORDER_SIDE.BUY &&
                        values.priceType === PRICE_TYPE.MARKET && (
                            <Input
                                disabled={!allowTrading}
                                name="amount"
                                label={t('amount')}
                                containerStyles={{
                                    paddingRight: 0,
                                }}
                                value={values.amount}
                                error={errors['amount']}
                                onChange={!allowTrading ? undefined : handleChange}
                                onBlur={handleBlur}
                                fullWidth
                                rightElement={<InputRightLabel>{quoteTokenName}</InputRightLabel>}
                            />
                        )}
                    <Range
                        disabled={!allowTrading}
                        name="percentage"
                        value={values.percentage}
                        onChange={handleChange}
                    />
                    {values.priceType !== PRICE_TYPE.MARKET && (
                        <Input
                            disabled={!allowTrading}
                            name="amount"
                            label={t('amount')}
                            containerStyles={{
                                paddingRight: 0,
                            }}
                            value={values.amount}
                            error={errors['amount']}
                            onChange={!allowTrading ? undefined : handleChange}
                            onBlur={handleBlur}
                            fullWidth
                            rightElement={<InputRightLabel>{quoteTokenName}</InputRightLabel>}
                        />
                    )}
                    {isAuthenticated && isMobile && (
                        <>
                            <FlexContainer
                                className="info-container"
                                styles={{ marginBottom: '2px !important' }}
                            >
                                <LabelText>
                                    {`${quoteTokenName} ${t('available_balance')}`}
                                </LabelText>
                                <ValueText>
                                    {roundNumber(
                                        Number(quoteAssets ? quoteAssets.free : 0),
                                        maxMarginDecimals,
                                    )}{' '}
                                    {quoteTokenName}
                                </ValueText>
                            </FlexContainer>
                            <FlexContainer className="info-container">
                                <LabelText>
                                    {`${baseTokenName} ${t('available_balance')}`}
                                </LabelText>
                                <ValueText>
                                    {roundNumber(
                                        Number(baseAssets ? baseAssets.free : 0),
                                        maxTradeQuantityDecimals,
                                    )}{' '}
                                    {baseTokenName}
                                </ValueText>
                            </FlexContainer>
                        </>
                    )}
                    {isAuthenticated ? (
                        <FlexContainer className="buttons-container">
                            <Button
                                type="button"
                                fullWidth
                                disabled={createOrderLoading || !allowTrading}
                                loading={
                                    (orderButtonRef.current === CREATE_ORDER_SIDE.BUY ||
                                        orderButtonRef.current === CREATE_ORDER_SIDE.SELL) &&
                                    createOrderLoading
                                }
                                className={`${
                                    values.orderSide === CREATE_ORDER_SIDE.BUY ? 'bg-up' : 'bg-down'
                                } OrderBtn`}
                                onClick={() => {
                                    orderButtonRef.current = updatedValues.orderSide;
                                    handleFormSubmit();
                                }}
                            >
                                {`${t(values.orderSide.toLowerCase())} ${baseTokenName}`}
                            </Button>
                        </FlexContainer>
                    ) : (
                        <FlexContainer className="buttons-container">
                            <Button
                                styles={{
                                    color: `${colors.primary} !important`,
                                }}
                                type="button"
                                className="left-button"
                                fullWidth
                                variant="outlined"
                                onClick={() => setCurrentUrl(APP_URLS.LOGIN)}
                            >
                                {t('login')}
                            </Button>
                            <Button
                                className="right-button"
                                type="button"
                                fullWidth
                                styles={{
                                    color: `${colors.brand.secondary} !important`,
                                }}
                                onClick={() => setCurrentUrl(APP_URLS.REGISTER)}
                            >
                                {t('signup')}
                            </Button>
                        </FlexContainer>
                    )}
                </div>
            </StyledForm>
            {isOrderConfirmationOpen && (
                <ConfirmationModal
                    handleClose={toggleOrderConfirmation}
                    handleSubmit={handleSubmit}
                    currentSymbol={currentSymbol}
                    values={values}
                    isOpen={isOrderConfirmationOpen}
                    higherPriceCheck={orderConfirmationCheckHigher}
                    lowerPriceCheck={orderConfirmationCheckLower}
                    triggerPriceCheck={orderConfirmationCheckTrigger}
                    orderFee={totalFee}
                />
            )}
        </>
    );
};

export { Form };
